// M03 聯立二元一次方程 - 主數據文件
// Simultaneous Linear Equations in Two Variables - Main Data File
// 系統化設計，準備後續 Supabase 數據遷移

// 導入分割數據文件
import {
  M03_01_PROBLEMS,
  M03_02_PROBLEMS,
  M03_03_PROBLEMS,
  M03_04_PROBLEMS,
  M03_05_PROBLEMS
} from './M03_Topics_01_to_05.js';

import {
  M03_06_PROBLEMS,
  M03_07_PROBLEMS,
  M03_08_PROBLEMS,
  M03_09_PROBLEMS,
  M03_10_PROBLEMS
} from './M03_Topics_06_to_10.js';

import {
  M03_11_PROBLEMS,
  M03_12_PROBLEMS,
  M03_13_PROBLEMS,
  M03_14_PROBLEMS,
  M03_15_PROBLEMS,
  M03_16_PROBLEMS
} from './M03_Topics_11_to_16.js';

// M03 模組基本信息
export const M03_MODULE_INFO = {
  id: "M03",
  title: "聯立二元一次方程",
  titleEn: "Simultaneous Linear Equations in Two Variables",
  description: "學習聯立二元一次方程的各種解法，包括代入法、消元法、圖形解法，以及在實際問題中的應用",
  difficulty: "中級",
  totalTopics: 16,
  totalProblems: 160,
  estimatedHours: 8,
  prerequisites: ["M01 數系與近似值", "M02 恆等式"],
  applications: ["價格問題", "速度時間問題", "工程問題", "經濟模型"],
  learningObjectives: [
    "掌握聯立方程的基本概念和解法",
    "能運用不同方法求解聯立方程",
    "理解解的幾何意義和存在性",
    "應用聯立方程解決實際問題"
  ]
};

// M03 主題列表
export const M03_TOPICS = [
  {
    id: "M03_01",
    title: "代入/消元法",
    titleEn: "Substitution/Elimination Method",
    description: "學習使用代入法和消元法求解聯立二元一次方程",
    difficulty: 1,
    estimatedMinutes: 25,
    problemCount: 10,
    keySkills: ["代入法", "消元法", "方程變形"],
    prerequisites: ["基本代數運算"]
  },
  {
    id: "M03_02",
    title: "圖形解法",
    titleEn: "Graphical Method",
    description: "通過繪製直線圖形來求解聯立方程，理解解的幾何意義",
    difficulty: 2,
    estimatedMinutes: 30,
    problemCount: 10,
    keySkills: ["直線繪製", "交點求解", "圖形分析"],
    prerequisites: ["直角坐標系", "直線方程"]
  },
  {
    id: "M03_03",
    title: "解的情況分析",
    titleEn: "Solution Analysis",
    description: "分析聯立方程解的存在性：唯一解、無解、無限多解",
    difficulty: 3,
    estimatedMinutes: 35,
    problemCount: 10,
    keySkills: ["解的判斷", "係數分析", "幾何解釋"],
    prerequisites: ["直線平行與相交"]
  },
  {
    id: "M03_04",
    title: "基本聯立方程",
    titleEn: "Basic Simultaneous Equations",
    description: "練習標準形式的聯立二元一次方程求解",
    difficulty: 1,
    estimatedMinutes: 20,
    problemCount: 10,
    keySkills: ["標準求解", "驗算", "解的表示"],
    prerequisites: ["基本代數"]
  },
  {
    id: "M03_05",
    title: "價格問題建模",
    titleEn: "Price Problem Modeling",
    description: "運用聯立方程解決商品價格、成本利潤等經濟問題",
    difficulty: 2,
    estimatedMinutes: 40,
    problemCount: 10,
    keySkills: ["問題建模", "變數設定", "實際應用"],
    prerequisites: ["百分法", "比例"]
  },
  {
    id: "M03_06",
    title: "速度時間問題",
    titleEn: "Speed and Time Problems",
    description: "解決涉及速度、時間、距離的運動問題",
    difficulty: 2,
    estimatedMinutes: 35,
    problemCount: 10,
    keySkills: ["運動分析", "時間計算", "相對運動"],
    prerequisites: ["速度公式", "時間單位"]
  },
  {
    id: "M03_07",
    title: "參數方程求解",
    titleEn: "Parametric Equation Solutions",
    description: "處理含有參數的聯立方程，分析參數對解的影響",
    difficulty: 3,
    estimatedMinutes: 45,
    problemCount: 10,
    keySkills: ["參數分析", "條件討論", "解的依賴性"],
    prerequisites: ["參數概念", "條件判斷"]
  },
  {
    id: "M03_08",
    title: "解的存在性分析",
    titleEn: "Solution Existence Analysis",
    description: "深入分析聯立方程解的存在性和唯一性條件",
    difficulty: 4,
    estimatedMinutes: 50,
    problemCount: 10,
    keySkills: ["存在性判斷", "理論分析", "條件推導"],
    prerequisites: ["線性代數基礎"]
  },
  {
    id: "M03_09",
    title: "幾何解釋",
    titleEn: "Geometric Interpretation",
    description: "從幾何角度理解聯立方程的解，直線的交點與平行關係",
    difficulty: 3,
    estimatedMinutes: 40,
    problemCount: 10,
    keySkills: ["幾何分析", "直線關係", "視覺化理解"],
    prerequisites: ["解析幾何", "直線方程"]
  },
  {
    id: "M03_10",
    title: "分數方程處理",
    titleEn: "Fractional Equation Handling",
    description: "處理含有分數係數的聯立方程，學習化簡技巧",
    difficulty: 2,
    estimatedMinutes: 30,
    problemCount: 10,
    keySkills: ["分數化簡", "通分技巧", "整數化"],
    prerequisites: ["分數運算", "最小公倍數"]
  },
  {
    id: "M03_11",
    title: "整數化轉換",
    titleEn: "Integer Conversion",
    description: "將分數係數方程轉換為整數係數，簡化計算過程",
    difficulty: 2,
    estimatedMinutes: 25,
    problemCount: 10,
    keySkills: ["係數轉換", "最小公倍數", "等價變換"],
    prerequisites: ["分數運算"]
  },
  {
    id: "M03_12",
    title: "解法比較",
    titleEn: "Solution Method Comparison",
    description: "比較不同解法的優缺點，選擇最適合的求解方法",
    difficulty: 3,
    estimatedMinutes: 35,
    problemCount: 10,
    keySkills: ["方法選擇", "效率分析", "策略思考"],
    prerequisites: ["多種解法掌握"]
  },
  {
    id: "M03_13",
    title: "三變數求解",
    titleEn: "Three-Variable Solutions",
    description: "擴展到三元聯立方程的求解方法和技巧",
    difficulty: 4,
    estimatedMinutes: 60,
    problemCount: 10,
    keySkills: ["三元消元", "系統化求解", "複雜運算"],
    prerequisites: ["二元方程熟練"]
  },
  {
    id: "M03_14",
    title: "方程組建模",
    titleEn: "System Modeling",
    description: "根據實際問題建立聯立方程組，培養建模能力",
    difficulty: 3,
    estimatedMinutes: 45,
    problemCount: 10,
    keySkills: ["問題分析", "變數識別", "約束建立"],
    prerequisites: ["邏輯分析", "實際問題理解"]
  },
  {
    id: "M03_15",
    title: "一致性分析",
    titleEn: "Consistency Analysis",
    description: "分析方程組的一致性，判斷解的合理性和可行性",
    difficulty: 4,
    estimatedMinutes: 50,
    problemCount: 10,
    keySkills: ["一致性判斷", "矛盾檢測", "理論分析"],
    prerequisites: ["邏輯推理", "數學證明"]
  },
  {
    id: "M03_16",
    title: "方程構造",
    titleEn: "Equation Construction",
    description: "根據給定條件構造聯立方程，逆向思維訓練",
    difficulty: 4,
    estimatedMinutes: 55,
    problemCount: 10,
    keySkills: ["逆向思維", "條件轉換", "方程設計"],
    prerequisites: ["綜合分析能力"]
  }
];

// 合併所有問題數據
export const M03_PROBLEMS_DATA = {
  "M03_01": M03_01_PROBLEMS,
  "M03_02": M03_02_PROBLEMS,
  "M03_03": M03_03_PROBLEMS,
  "M03_04": M03_04_PROBLEMS,
  "M03_05": M03_05_PROBLEMS,
  "M03_06": M03_06_PROBLEMS,
  "M03_07": M03_07_PROBLEMS,
  "M03_08": M03_08_PROBLEMS,
  "M03_09": M03_09_PROBLEMS,
  "M03_10": M03_10_PROBLEMS,
  "M03_11": M03_11_PROBLEMS,
  "M03_12": M03_12_PROBLEMS,
  "M03_13": M03_13_PROBLEMS,
  "M03_14": M03_14_PROBLEMS,
  "M03_15": M03_15_PROBLEMS,
  "M03_16": M03_16_PROBLEMS
};

// 輔助函數：根據主題ID獲取問題
export const getProblemsByTopicId = (topicId) => {
  return M03_PROBLEMS_DATA[topicId] || [];
};

// 輔助函數：獲取所有M03問題
export const getAllM03Problems = () => {
  return Object.values(M03_PROBLEMS_DATA).flat();
};

// 輔助函數：根據難度獲取問題
export const getProblemsByDifficulty = (difficulty) => {
  return getAllM03Problems().filter(problem => problem.difficulty === difficulty);
};

// 輔助函數：獲取主題信息
export const getTopicById = (topicId) => {
  return M03_TOPICS.find(topic => topic.id === topicId);
};

// 輔助函數：搜索問題
export const searchProblems = (keyword) => {
  const allProblems = getAllM03Problems();
  return allProblems.filter(problem => 
    problem.question.includes(keyword) || 
    problem.explanation.includes(keyword) ||
    problem.hint.includes(keyword)
  );
};

// M03 統計信息
export const M03_STATISTICS = {
  totalProblems: getAllM03Problems().length,
  totalTopics: M03_TOPICS.length,
  difficultyDistribution: {
    level1: getProblemsByDifficulty(1).length,
    level2: getProblemsByDifficulty(2).length,
    level3: getProblemsByDifficulty(3).length,
    level4: getProblemsByDifficulty(4).length
  },
  averageDifficulty: (
    getAllM03Problems().reduce((sum, problem) => sum + problem.difficulty, 0) / 
    getAllM03Problems().length
  ).toFixed(1),
  estimatedStudyTime: M03_TOPICS.reduce((sum, topic) => sum + topic.estimatedMinutes, 0),
  topicsByDifficulty: {
    beginner: M03_TOPICS.filter(topic => topic.difficulty === 1).length,
    intermediate: M03_TOPICS.filter(topic => topic.difficulty === 2).length,
    advanced: M03_TOPICS.filter(topic => topic.difficulty === 3).length,
    expert: M03_TOPICS.filter(topic => topic.difficulty === 4).length
  },
  keyFeatures: [
    "完整覆蓋16個核心主題",
    "系統化160題練習",
    "多種解法比較",
    "實際問題應用",
    "理論與實踐結合",
    "難度循序漸進"
  ]
};

// 數據驗證函數
export const validateM03Data = () => {
  const issues = [];
  
  // 檢查每個主題的問題數量
  M03_TOPICS.forEach(topic => {
    const problems = getProblemsByTopicId(topic.id);
    if (problems.length !== 10) {
      issues.push(`主題 ${topic.id} 問題數量不正確: ${problems.length}/10`);
    }
  });
  
  // 檢查問題ID唯一性
  const allProblems = getAllM03Problems();
  const ids = allProblems.map(p => p.id);
  const uniqueIds = [...new Set(ids)];
  if (ids.length !== uniqueIds.length) {
    issues.push('存在重複的問題ID');
  }
  
  // 檢查必需字段
  allProblems.forEach(problem => {
    const requiredFields = ['id', 'topicId', 'question', 'solution', 'explanation', 'hint', 'difficulty', 'order', 'steps'];
    requiredFields.forEach(field => {
      if (!problem[field]) {
        issues.push(`問題 ${problem.id} 缺少字段: ${field}`);
      }
    });
  });
  
  return {
    isValid: issues.length === 0,
    issues,
    summary: {
      totalProblems: allProblems.length,
      totalTopics: M03_TOPICS.length,
      validationTime: new Date().toISOString()
    }
  };
};

// 導出所有數據和函數
export default {
  M03_MODULE_INFO,
  M03_TOPICS,
  M03_PROBLEMS_DATA,
  getAllM03Problems,
  getProblemsByTopicId,
  getProblemsByDifficulty,
  getTopicById,
  searchProblems,
  M03_STATISTICS,
  validateM03Data
}; 